import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const useScrollEnd = (dep) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [dep]);
  return ref;
};

const SendBar = ({ placeholder, label, onSend, disabled, mono, id }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!v.trim() || busy) return;
    setBusy(true);
    if (await onSend(v.trim())) setV("");
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2 border rounded-full bg-white pl-4 pr-1 py-1 mt-3">
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} disabled={disabled}
        placeholder={disabled ? "Poste hors ligne" : placeholder} className={`flex-1 min-w-0 outline-none text-sm bg-transparent ${mono ? "font-mono" : ""}`} data-testid={`${id}-input`} />
      <button onClick={go} disabled={disabled || busy || !v.trim()} data-testid={`${id}-send-btn`}
        className="rounded-full bg-blue-600 text-white text-sm font-semibold px-4 h-8 disabled:bg-slate-300">{busy ? "…" : label}</button>
    </div>
  );
};

// Messages with the PC user, without opening a remote session.
export const ChatTab = ({ m }) => {
  const [msgs, setMsgs] = useState([]);
  const load = useCallback(() => api.get(`/machines/${m.machine_id}/chat`).then(({ data }) => setMsgs(data)).catch(() => {}), [m.machine_id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const ref = useScrollEnd(msgs.length);
  const send = (text) => api.post(`/machines/${m.machine_id}/message`, { text }).then(() => { load(); return true; }).catch((e) => { toast.error(errMsg(e)); return false; });
  return (
    <div className="flex flex-col h-full min-h-[420px]" data-testid="machine-chat-tab">
      <div ref={ref} className="flex-1 overflow-y-auto space-y-2">
        {msgs.map((x, i) => (
          <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[90%] ${x.sender === "tech" ? "bg-blue-50 ml-auto" : "bg-slate-100"}`}>
            <span className="font-semibold">{x.name}</span> <span className="text-xs text-slate-500">{fmtDate(x.at)}</span>
            <p className="whitespace-pre-wrap">{x.text}</p>
          </div>
        ))}
        {!msgs.length && (
          <div className="text-center text-slate-500 pt-10">
            <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="font-medium text-slate-700">Aucun message échangé pour le moment</p>
            <p className="text-sm mt-1">Le message s'affiche dans une fenêtre sur le poste. L'utilisateur peut répondre.</p>
          </div>
        )}
      </div>
      <SendBar id="chat" placeholder="Saisir un message" label="Envoyer" onSend={send} disabled={!m.online} />
    </div>
  );
};

// Commands run on the PC without a session, with their output.
export const CommandsTab = ({ m }) => {
  const [rows, setRows] = useState([]);
  const win = m.os === "Windows";
  const [shell, setShell] = useState("cmd");
  const load = useCallback(() => api.get(`/machines/${m.machine_id}/commands`).then(({ data }) => setRows(data)).catch(() => {}), [m.machine_id]);
  useEffect(() => { load(); }, [load]);
  const ref = useScrollEnd(rows.length);
  const run = async (command) => {
    setRows((r) => [...r, { log_id: "pending", user_name: "…", command, at: new Date().toISOString(), pending: true }]);
    try { await api.post(`/machines/${m.machine_id}/command`, { command, shell: win ? shell : "auto" }, { timeout: 140000 }); return true; }
    catch (e) { toast.error(errMsg(e)); return false; } finally { load(); }
  };
  const del = (r) => api.delete(`/machines/${m.machine_id}/commands/${r.log_id}`).then(load).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-col h-full min-h-[420px]" data-testid="machine-commands-tab">
      <div ref={ref} className="flex-1 overflow-y-auto divide-y border rounded-md bg-white">
        {rows.map((r) => (
          <div key={r.log_id} className="p-3 font-mono text-[12px]" data-testid={`command-row-${r.log_id}`}>
            <div className="flex items-center gap-2 font-sans text-[12px]">
              <b>{r.user_name}</b><span className="text-slate-500">{fmtDate(r.at)}</span>
              {r.pending ? <span className="text-slate-400">exécution…</span> : r.code === undefined ? null
                : r.ok ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-500" />}
              {!r.pending && <button onClick={() => del(r)} className="ml-auto text-slate-400 hover:text-red-600" data-testid={`command-delete-${r.log_id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <p className="mt-1 text-slate-800 break-all">&gt; {r.command}</p>
            {r.output && <pre className="mt-1 whitespace-pre-wrap break-all text-slate-600 max-h-48 overflow-auto">{r.output}</pre>}
          </div>
        ))}
        {!rows.length && <p className="p-6 text-center text-sm text-slate-500">Aucune commande. Elles s'exécutent avec les droits administrateur, sans ouvrir de prise en main.</p>}
      </div>
      {win && (
        <div className="flex gap-4 text-xs mt-2 text-slate-600">
          {[["cmd", "Invite de commandes"], ["powershell", "PowerShell"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1"><input type="radio" checked={shell === k} onChange={() => setShell(k)} data-testid={`commands-shell-${k}`} />{l}</label>
          ))}
        </div>
      )}
      <SendBar id="commands" mono placeholder="Saisir une commande" label="Exécuter" onSend={run} disabled={!m.online} />
    </div>
  );
};
